import { useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '@/api/client';
import { requireReason, validateComponentChanges } from '@/api/mock/validate';
import { useSaveConfig } from '@/api/queries';
import { configPath } from '@/app/scope';
import { useToast } from '@/app/toasts';
import { Button } from '@/components/Button';
import { useFocusFirstInvalid } from '@/components/focus';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/fields';
import { Cross, Plus } from '@/components/icons';
import { Kv, Notice, Panel } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { HEALTH_LABEL, ladderSteps } from '@/domain/health';
import type {
  ComponentConfig,
  ComponentConfigChanges,
  Health,
  Ladder,
  Scope,
  Violation,
} from '@/domain/types';
import { ApproverField, ErrorSummary } from '@/features/proposals/decision';
import { explainError, useApproverName } from '@/features/proposals/decisionState';

import {
  effectsOf,
  isComponentConfig,
  parseLines,
  sameArray,
  sameLadder,
  useBeforeUnload,
  wallClock,
} from './helpers';
import { ReasonField, StaleNotice } from './shared';

const LADDER_HEALTHS: Health[] = ['impaired', 'degraded', 'failed'];

interface LadderRow {
  step: string;
  health: Health;
}

/** Form values as typed, so half-typed numbers do not fight the keyboard. */
interface Draft {
  display_name: string;
  source: string;
  checksText: string;
  ladder: LadderRow[];
  recovery_threshold_runs: string;
  auto_recovery: boolean;
  recovery_approval_alert_min: string;
  runner_cadence_minutes: string;
  staleness_tolerance_cycles: string;
}

function draftFrom(config: ComponentConfig): Draft {
  return {
    display_name: config.display_name,
    source: config.source,
    checksText: config.checks.join('\n'),
    ladder: ladderSteps(config.ladder).map((s) => ({ step: String(s.step), health: s.health })),
    recovery_threshold_runs: String(config.recovery_threshold_runs),
    auto_recovery: config.auto_recovery,
    recovery_approval_alert_min: String(config.recovery_approval_alert_min),
    runner_cadence_minutes: String(config.runner_cadence_minutes),
    staleness_tolerance_cycles: String(config.staleness_tolerance_cycles),
  };
}

function toInt(text: string): number {
  return text.trim() === '' ? Number.NaN : Number(text);
}

function ladderFromRows(rows: LadderRow[]): Ladder {
  const ladder: Ladder = {};
  for (const row of rows) ladder[row.step.trim()] = row.health;
  return ladder;
}

/** Only the fields that differ from the loaded row are sent (TDD §11.2 config save body). */
function changesFrom(current: ComponentConfig, draft: Draft): ComponentConfigChanges {
  const changes: ComponentConfigChanges = {};
  if (draft.display_name !== current.display_name) changes.display_name = draft.display_name;
  if (draft.source !== current.source) changes.source = draft.source;
  const checks = parseLines(draft.checksText);
  if (!sameArray(checks, current.checks)) changes.checks = checks;
  const ladder = ladderFromRows(draft.ladder);
  if (!sameLadder(ladder, current.ladder)) changes.ladder = ladder;
  const recovery = toInt(draft.recovery_threshold_runs);
  if (recovery !== current.recovery_threshold_runs) changes.recovery_threshold_runs = recovery;
  if (draft.auto_recovery !== current.auto_recovery) changes.auto_recovery = draft.auto_recovery;
  const alert = toInt(draft.recovery_approval_alert_min);
  if (alert !== current.recovery_approval_alert_min) changes.recovery_approval_alert_min = alert;
  const cadence = toInt(draft.runner_cadence_minutes);
  if (cadence !== current.runner_cadence_minutes) changes.runner_cadence_minutes = cadence;
  const tolerance = toInt(draft.staleness_tolerance_cycles);
  if (tolerance !== current.staleness_tolerance_cycles)
    changes.staleness_tolerance_cycles = tolerance;
  return changes;
}

export function ConfigForm({ scope, config }: { scope: Scope; config: ComponentConfig }) {
  const [baseline, setBaseline] = useState<ComponentConfig>(config);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(config));
  const [reason, setReason] = useState('');
  const approver = useApproverName();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, fieldErrors);
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ComponentConfig | null>(null);
  const mutation = useSaveConfig(scope, config.capability_id);
  const toast = useToast();
  const ladderLabelId = useId();
  const ladderHintId = useId();

  const changes = changesFrom(baseline, draft);
  const changedKeys = Object.keys(changes);
  const dirty = changedKeys.length > 0 || reason.trim().length > 0;
  useBeforeUnload(dirty);

  // The row moved under the editor: a 409 on save, or a refetch that brought a newer version.
  const stale = conflict ?? (config.version !== baseline.version ? config : null);
  const effects = effectsOf(changedKeys);
  const cadence = toInt(draft.runner_cadence_minutes);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setRow = (index: number, patch: Partial<LadderRow>) =>
    setDraft((d) => ({
      ...d,
      ladder: d.ladder.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  const removeRow = (index: number) =>
    setDraft((d) => ({ ...d, ladder: d.ladder.filter((_, i) => i !== index) }));
  const addRow = () =>
    setDraft((d) => {
      const last = d.ladder[d.ladder.length - 1];
      const lastStep = last ? toInt(last.step) : 0;
      const nextHealth =
        LADDER_HEALTHS[Math.min(d.ladder.length, LADDER_HEALTHS.length - 1)] ?? 'failed';
      return {
        ...d,
        ladder: [
          ...d.ladder,
          { step: Number.isFinite(lastStep) ? String(lastStep * 2 || 3) : '', health: nextHealth },
        ],
      };
    });

  const reset = (next: ComponentConfig) => {
    setBaseline(next);
    setDraft(draftFrom(next));
    setConflict(null);
    setFieldErrors({});
    setMessage(null);
  };

  const submit = async () => {
    setFieldErrors({});
    setMessage(null);
    if (changedKeys.length === 0) {
      setMessage('Nothing has changed yet. Edit a field, then save.');
      return;
    }
    const violations: Violation[] = [
      ...validateComponentChanges(baseline, changes),
      ...requireReason(reason),
    ];
    const steps = draft.ladder.map((row) => row.step.trim());
    if (new Set(steps).size !== steps.length) {
      violations.push({
        field: 'ladder',
        message: 'Each ladder step must be a different number of runs.',
      });
    }
    if (!approver.name.trim())
      violations.push({ field: 'changed_by', message: 'Say who is making the change.' });
    if (violations.length > 0) {
      const byField: Record<string, string> = {};
      for (const v of violations) byField[v.field] ??= v.message;
      setFieldErrors(byField);
      return;
    }
    approver.remember();
    try {
      const { config: saved } = await mutation.mutateAsync({
        version: baseline.version,
        changes,
        changed_by: approver.name.trim(),
        reason: reason.trim(),
      });
      if (isComponentConfig(saved)) {
        toast.push({
          tone: 'success',
          title: `Saved version ${saved.version} of ${saved.display_name}`,
          ...(effects[0] ? { description: effects[0] } : {}),
        });
        reset(saved);
        setReason('');
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const current = error.conflict?.current;
        if (isComponentConfig(current)) setConflict(current);
        else setMessage(error.message);
        return;
      }
      const explained = explainError(error);
      setFieldErrors(explained.fields);
      setMessage(explained.message);
    }
  };

  const disabled = mutation.isPending;

  return (
    <form
      ref={formRef}
      className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px] items-start"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <div className="flex flex-col gap-5 min-w-0">
        {stale ? (
          <StaleNotice current={stale} onLoad={() => reset(stale)} disabled={disabled} />
        ) : null}

        <Panel title="Identity" divided={false}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Display name"
              required
              error={fieldErrors.display_name}
              hint="Customer-facing."
            >
              {(props) => (
                <Input
                  {...props}
                  name="display_name"
                  autoComplete="off"
                  value={draft.display_name}
                  onChange={(e) => set({ display_name: e.target.value })}
                  disabled={disabled}
                />
              )}
            </Field>
            <Field
              label="Source"
              required
              error={fieldErrors.source}
              hint="The runner identity allowed to post runs."
            >
              {(props) => (
                <Input
                  {...props}
                  name="source"
                  autoComplete="off"
                  spellCheck={false}
                  value={draft.source}
                  onChange={(e) => set({ source: e.target.value })}
                  disabled={disabled}
                />
              )}
            </Field>
          </div>
        </Panel>

        <Panel title="Checks" divided={false}>
          <Field
            label="Check names"
            required
            error={fieldErrors.checks}
            hint="One per line, in the order the runner performs them."
          >
            {(props) => (
              <Textarea
                {...props}
                name="checks"
                rows={Math.max(4, draft.ladder.length + 2)}
                spellCheck={false}
                value={draft.checksText}
                onChange={(e) => set({ checksText: e.target.value })}
                disabled={disabled}
              />
            )}
          </Field>
        </Panel>

        {/* The runner's rhythm comes before the ladder: each ladder step's wall-clock hint
            (≈ 10 min) is this cadence multiplied, so the reader meets the cadence first. */}
        <Panel title="Runner" divided={false}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Runner cadence (minutes)"
              required
              error={fieldErrors.runner_cadence_minutes}
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  name="runner_cadence_minutes"
                  value={draft.runner_cadence_minutes}
                  onChange={(e) => set({ runner_cadence_minutes: e.target.value })}
                  disabled={disabled}
                  className="tabular max-w-40"
                />
              )}
            </Field>
            <Field
              label="Staleness tolerance (missed runs)"
              required
              error={fieldErrors.staleness_tolerance_cycles}
              hint={
                Number.isFinite(cadence) && Number.isFinite(toInt(draft.staleness_tolerance_cycles))
                  ? `Blind ${wallClock(toInt(draft.staleness_tolerance_cycles), cadence) ?? '—'} after the last run.`
                  : undefined
              }
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  name="staleness_tolerance_cycles"
                  value={draft.staleness_tolerance_cycles}
                  onChange={(e) => set({ staleness_tolerance_cycles: e.target.value })}
                  disabled={disabled}
                  className="tabular max-w-40"
                />
              )}
            </Field>
          </div>
        </Panel>

        <Panel title="Escalation ladder" divided={false}>
          <div
            role="group"
            aria-labelledby={ladderLabelId}
            aria-describedby={ladderHintId}
            className="flex flex-col gap-2"
          >
            <span id={ladderLabelId} className="sr-only">
              Ladder steps
            </span>
            <span id={ladderHintId} className="sr-only">
              Each step is a number of consecutive failing runs and the status the page shows from
              then on.
            </span>
            {draft.ladder.length === 0 ? (
              <p className="text-body text-ink-3">No steps: add at least one.</p>
            ) : null}
            {draft.ladder.map((row, index) => {
              const clock = wallClock(toInt(row.step), cadence);
              return (
                <div key={index} className="flex flex-wrap items-center gap-2 text-body">
                  <span className="text-ink-2">After</span>
                  {/* The controls default to full width; the wrappers give the row its shape. */}
                  <div className="w-24">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step={1}
                      aria-label={`Failing runs for step ${index + 1}`}
                      value={row.step}
                      onChange={(e) => setRow(index, { step: e.target.value })}
                      disabled={disabled}
                      className="tabular"
                    />
                  </div>
                  <span className="text-ink-2">failing runs, show</span>
                  <div className="w-60">
                    <Select
                      aria-label={`Status for step ${index + 1}`}
                      value={row.health}
                      onChange={(e) => setRow(index, { health: e.target.value as Health })}
                      disabled={disabled}
                    >
                      {LADDER_HEALTHS.map((h) => (
                        <option key={h} value={h}>
                          {HEALTH_LABEL[h]}
                        </option>
                      ))}
                    </Select>
                  </div>
                  {clock ? <span className="text-ink-3 tabular">≈ {clock}</span> : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove step ${index + 1}`}
                    onClick={() => removeRow(index)}
                    disabled={disabled}
                    className="px-1.5 ml-auto"
                  >
                    <Cross size={14} />
                  </Button>
                </div>
              );
            })}
            <div>
              <Button size="sm" icon={<Plus size={14} />} onClick={addRow} disabled={disabled}>
                Add step
              </Button>
            </div>
            {fieldErrors.ladder ? (
              <p className="text-meta text-danger-ink font-medium" role="alert">
                {fieldErrors.ladder}
              </p>
            ) : null}
          </div>
        </Panel>

        <Panel title="Recovery" divided={false}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Consecutive passing runs to recover"
              required
              error={fieldErrors.recovery_threshold_runs}
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  name="recovery_threshold_runs"
                  value={draft.recovery_threshold_runs}
                  onChange={(e) => set({ recovery_threshold_runs: e.target.value })}
                  disabled={disabled}
                  className="tabular max-w-40"
                />
              )}
            </Field>
            <Field
              label="Recovery approval alert (minutes)"
              required
              error={fieldErrors.recovery_approval_alert_min}
              hint="A waiting recovery is flagged as overdue after this long."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1}
                  name="recovery_approval_alert_min"
                  value={draft.recovery_approval_alert_min}
                  onChange={(e) => set({ recovery_approval_alert_min: e.target.value })}
                  disabled={disabled}
                  className="tabular max-w-40"
                />
              )}
            </Field>
          </div>
          <div className="mt-4">
            <Checkbox
              name="auto_recovery"
              label="Recover automatically"
              hint="Otherwise a person confirms each recovery in the queue."
              checked={draft.auto_recovery}
              onChange={(e) => set({ auto_recovery: e.target.checked })}
              disabled={disabled}
            />
          </div>
        </Panel>

        <Panel title="Save" divided={false}>
          <div className="flex flex-col gap-4">
            {effects.length > 0 ? (
              <Notice tone="info">
                {effects.length === 1 ? (
                  effects[0]
                ) : (
                  <ul className="list-disc pl-5 flex flex-col gap-0.5">
                    {effects.map((effect) => (
                      <li key={effect}>{effect}</li>
                    ))}
                  </ul>
                )}
              </Notice>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_240px]">
              <ReasonField
                value={reason}
                onChange={setReason}
                error={fieldErrors.reason}
                disabled={disabled}
              />
              <ApproverField
                label="Changed by"
                value={approver.name}
                onChange={approver.setName}
                error={fieldErrors.changed_by}
                disabled={disabled}
              />
            </div>
            <ErrorSummary message={message} />
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="primary" type="submit" loading={disabled} disabled={Boolean(stale)}>
                Save changes
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  reset(baseline);
                  setReason('');
                }}
                disabled={disabled || !dirty}
              >
                Discard edits
              </Button>
            </div>
          </div>
        </Panel>
      </div>

      <aside className="flex flex-col gap-4 lg:sticky lg:top-4">
        <Panel title="This version" bodyClassName="px-5 py-4">
          <Kv
            items={[
              { label: 'Version', value: <span className="tabular">{baseline.version}</span> },
              { label: 'Updated', value: <Timestamp iso={baseline.updated_at} mode="full" /> },
              { label: 'Updated by', value: baseline.updated_by },
            ]}
          />
          <p className="mt-4 text-body">
            <Link to={configPath(scope, baseline.capability_id, 'history')}>Version history</Link>
          </p>
        </Panel>
      </aside>
    </form>
  );
}
